import React, {
  useState,
  useMemo,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Dropdown, Menu } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import classnames from "classnames";
// Context
import { PricerContext } from "contexts/PricerContext";
import { GlobalContext } from "contexts/GlobalContext";
// Helpers
import { PRICER_DATE_SELECTED } from "helpers/Pricer/utils";
import {
  ACTIONS,
  CATEGORIES,
  CONTEXTS,
  EVENT_NAMES,
  sendEvent,
} from "helpers/googleAnalyticsHelper";
import {
  calendarDate,
  isUtcToday,
  utcCalendarMoment,
  utcToday,
} from "helpers/utcCalendarDate";
// Toolbar pieces
import {
  ITEM_VARIANTS,
  MENU_GROUPS,
  PINNED_KEYS,
  PRICER_TOOLBAR_ITEMS,
  TOOLBAR_OVERFLOW_PRIORITY,
  getAvailableKeys,
} from "./pricerToolbarItems";
import { renderInlineItem, renderMenuItem } from "./PricerToolbarItemViews";
import useToolbarOverflow from "./useToolbarOverflow";
import usePricerTools from "./usePricerTools";
import usePricerToolbarActions from "./usePricerToolbarActions";
import useCommodityFilter from "./useCommodityFilter";
import PricerToolModals from "./PricerToolModals";
// Style
import "styles/PricerAuditAgGridLayout.scss";
import "./FilterButtons.scss";

// Imported from this path by other files; defined in ./constants.
export { FILTER_BUTTONS_NAMES, SUMMARY_IDS } from "./constants";

const FilterButtons = () => {
  //Context
  const {
    state: { pricerTablesData, pricerOptInfo, pricerRefAgGrid, dateSelected },
    isOptionRowLoading,
    isOptionRowRefetching,
    isOptionRowFetching,
    setDateSelected,
    setPricerOperation,
    setIsCalculationDateChanged,
    refetchOptionRow,
  } = useContext(PricerContext);
  const {
    globalState: { dark_Theme },
  } = useContext(GlobalContext);

  //States
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isCompactMenuOpen, setIsCompactMenuOpen] = useState(false);
  const menuOpenRef = useRef(false);
  const datePickerRef = useRef();

  const closeCompactMenu = useCallback(() => {
    menuOpenRef.current = false;
    setIsCompactMenuOpen(false);
  }, []);

  const pricerTools = usePricerTools();
  const { productsData, buttonSelected, onFilterButtonClick } =
    useCommodityFilter({
      pricerTablesData,
      pricerOptInfo,
      pricerRefAgGrid,
      setPricerOperation,
    });
  const { handlersByKey, openQuickAdd } = usePricerToolbarActions({
    pricerRefAgGrid,
    pricerTablesData,
    tools: pricerTools,
    closeCompactMenu,
    onFilterButtonClick,
  });

  const handleCompactCommodityClick = useCallback(
    (product) => {
      onFilterButtonClick(product);
      closeCompactMenu();
    },
    [onFilterButtonClick, closeCompactMenu]
  );

  const onDatePickerChange = (date, dateString) => {
    // Clear (x) returns to the UTC calendar day. A picked day stays the
    // YYYY-MM-DD from the picker; do not format the moment, which can move it.
    const finalDate =
      date == null || !dateString
        ? utcToday()
        : calendarDate(dateString) ?? utcToday();

    setPricerOperation({ ...pricerOptInfo, [PRICER_DATE_SELECTED]: finalDate });
    setDateSelected(finalDate);
    setIsDatePickerOpen(false);
    datePickerRef.current && datePickerRef.current.blur();
    setIsCalculationDateChanged(1);
    sendEvent({
      category: CATEGORIES.PRICER,
      context: CONTEXTS.PRICER,
      action: ACTIONS.CLICK,
      eventName: EVENT_NAMES.PRICER_CALCULATION_DATE_CHANGE,
      extraParams: {
        change_type: date === null || !dateString ? "clear" : "set",
      },
    });
  };
  const isDisabled =
    isOptionRowFetching || isOptionRowLoading || isOptionRowRefetching;

  // Toolbar items (declared once in pricerToolbarItems.js) with their
  // handlers, rendered both inline and in the "Pricer Tools" menu.
  const items = useMemo(
    () =>
      Object.fromEntries(
        PRICER_TOOLBAR_ITEMS.map((item) => [
          item.key,
          {
            ...item,
            onClick: handlersByKey[item.key],
            disabled: item.variant === ITEM_VARIANTS.DATE ? isDisabled : false,
          },
        ])
      ),
    [handlersByKey, isDisabled]
  );

  // Shared state the item views need.
  const view = {
    productsData,
    buttonSelected,
    onCompactCommodityClick: handleCompactCommodityClick,
    datePickerRef,
    datePickerClassName: classnames(
      "calc-data",
      dateSelected && !isUtcToday(dateSelected) && "filled--data__background"
    ),
    datePickerProps: {
      onOpenChange: setIsDatePickerOpen,
      value: dateSelected ? utcCalendarMoment(dateSelected) : "",
      onChange: onDatePickerChange,
      format: "YYYY-MM-DD",
      popupStyle: { background: "black" },
      popupClassName: dark_Theme || false ? "darkCalender" : "",
      style: { color: "white !important" },
      onClick: (event) => event.stopPropagation(),
    },
  };

  // Keys that are actually available (commodities/collapse-all need products).
  const hasProducts = productsData.length > 0;
  const availableKeys = useMemo(
    () => getAvailableKeys(hasProducts),
    [hasProducts]
  );

  const {
    visibleKeys,
    isToolbarReady,
    reserveRight,
    containerRef,
    measureRefs,
    menuMeasureRef,
    scheduleRecomputeOverflow,
  } = useToolbarOverflow(availableKeys, PINNED_KEYS, TOOLBAR_OVERFLOW_PRIORITY);

  useEffect(() => {
    scheduleRecomputeOverflow();
  }, [dateSelected, dark_Theme, buttonSelected, scheduleRecomputeOverflow]);

  const overflowKeys = useMemo(
    () => availableKeys.filter((key) => !visibleKeys.includes(key)),
    [availableKeys, visibleKeys]
  );

  const hasOverflow = overflowKeys.length > 0;
  const showOverflowMenu = isToolbarReady && (hasOverflow || isCompactMenuOpen);

  const handleOverflowMenuVisibleChange = useCallback(
    (open) => {
      menuOpenRef.current = open;
      setIsCompactMenuOpen(open);
      if (!open && !isDatePickerOpen) {
        scheduleRecomputeOverflow();
      }
    },
    [isDatePickerOpen, scheduleRecomputeOverflow]
  );

  const overflowMenuContent = (
    <Menu
      theme={dark_Theme ? "dark" : "light"}
      selectable={false}
      mode="vertical"
    >
      {MENU_GROUPS.map((group) => {
        const groupKeys = group.keys.filter((key) =>
          overflowKeys.includes(key)
        );
        if (groupKeys.length === 0) return null;
        return (
          <Menu.ItemGroup
            key={group.title}
            title={
              <span className="pricer-filter-compact-menu__group-title">
                {group.title}
              </span>
            }
          >
            {groupKeys.map((key) => renderMenuItem(items[key], view))}
          </Menu.ItemGroup>
        );
      })}
    </Menu>
  );

  const containerClassName = classnames(
    "buttons-container",
    "filter-buttons_container",
    "pricer-toolbar",
    dark_Theme || false
      ? "pricer-filter-buttons__container_dark"
      : "pricer-filter-buttons__container"
  );

  return (
    <div
      ref={containerRef}
      className={containerClassName}
      style={{ left: 15, right: reserveRight }}
    >
      {/* Visible (on-bar) items */}
      {availableKeys
        .filter((key) => visibleKeys.includes(key))
        .map((key) => (
          <React.Fragment key={key}>
            {renderInlineItem(items[key], view)}
          </React.Fragment>
        ))}

      {/* Overflow menu — Dropdown avoids Ant horizontal Menu auto-collapsing to "⋯" */}
      {showOverflowMenu && (
        <Dropdown
          trigger={["click"]}
          visible={isCompactMenuOpen || isDatePickerOpen}
          onVisibleChange={(open) => {
            if (isDatePickerOpen && !open) return;
            handleOverflowMenuVisibleChange(open);
          }}
          getPopupContainer={() => document.body}
          destroyPopupOnHide={false}
          overlay={overflowMenuContent}
          overlayClassName={classnames("pricer-filter-compact-menu-overlay", {
            "pricer-filter-compact-menu-overlay--dark": dark_Theme,
          })}
        >
          <span className="pricer-tools-trigger-wrap">
            <button
              type="button"
              className="pricer-tools-trigger"
              aria-haspopup="menu"
              aria-expanded={isCompactMenuOpen}
            >
              <MenuOutlined aria-hidden />
              <span className="pricer-tools-trigger__label">Pricer Tools</span>
            </button>
          </span>
        </Dropdown>
      )}

      {/* Offscreen measurer: always renders every available item to size them */}
      <div className="pricer-toolbar__measurer" aria-hidden>
        {availableKeys.map((key) => (
          <div
            key={key}
            ref={(el) => {
              measureRefs.current[key] = el;
            }}
            className="pricer-toolbar__measure-item"
          >
            {renderInlineItem(items[key], view, true)}
          </div>
        ))}
        <div
          ref={menuMeasureRef}
          className="pricer-toolbar__measure-item pricer-toolbar__measure-menu"
        >
          <MenuOutlined /> Pricer Tools
        </div>
      </div>

      <PricerToolModals
        tools={pricerTools}
        onQuickAddHotkey={openQuickAdd}
        refetchOptionRow={refetchOptionRow}
        dark_Theme={dark_Theme}
      />
    </div>
  );
};

export default FilterButtons;
